import { Button, Flex, Heading, Icon, Skeleton } from "@chakra-ui/react";
import React from "react";
import { RiShieldLine } from "react-icons/ri";
import { EmptyState } from "src/components/EmptyState";
import { GenericError } from "src/components/Feedback/GenericError";
import { NotFound } from "src/components/Feedback/NotFound";
import { AdminPage } from "src/components/Layout/AdminPage";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { AdminHeading } from "src/components/Text/AdminHeading";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import useCheckUserRole from "src/hooks/useCheckUserRole";
import {
  Factor,
  fetchMfaFactors,
  NeedReauthenticateError,
  useMfaApi,
} from "src/hooks/useMfaApi";
import { useOrganization } from "src/hooks/useOrganization";
import { useOrgConfig } from "src/hooks/useOrgConfig";
import * as BC from "src/services/breadcrumb";
import { useEnv } from "src/services/env";
import * as RD from "src/types/remoteData";
import { OtpFactorCard } from "./OtpFactorCard";
import { PhoneFactorCard } from "./PhoneFactorCard";

export const Security = () => {
  const organization = useOrganization();
  const env = useEnv();
  const toast = useAvelaToast();

  const [hasMfaTokenRD, setHasMfaTokenRD] = React.useState<
    RD.RemoteData<Error, boolean>
  >(RD.loading());

  const [factorsRD, setFactorsRD] = React.useState<
    RD.RemoteData<Error, Factor[]>
  >(RD.loading());

  const [isReauthenticating, setReauthenticating] = React.useState(false);
  const orgConfigRD = useOrgConfig<OrgConfig.MfaConfig>(
    "Mfa",
    OrgConfig.MfaConfigSchema.default
  );
  const mfaApi = useMfaApi();
  const { isAvelaAdmin } = useCheckUserRole();

  React.useEffect(() => {
    mfaApi
      .hasMfaToken()
      .then((hasMfaToken) => {
        setHasMfaTokenRD(RD.success(hasMfaToken));
      })
      .catch((error: unknown) => {
        setHasMfaTokenRD(RD.failure(toError(error)));
      });
  }, [mfaApi]);

  const reauthenticate = React.useCallback(async () => {
    if (!organization.hasData()) {
      toast.genericError();
      return;
    }

    setReauthenticating(true);
    await mfaApi.reauthenticate();
  }, [mfaApi, organization, toast]);

  const fetchFactors = React.useCallback(
    async (tokenOverride?: string) => {
      setFactorsRD((current) => RD.loading(current));
      try {
        const token = tokenOverride ?? (await mfaApi.getMfaAccessToken());
        const factors = await fetchMfaFactors(env.VITE_AUTH0_DOMAIN, token);
        setFactorsRD(RD.success(factors));
      } catch (error: unknown) {
        if (error instanceof NeedReauthenticateError) {
          setHasMfaTokenRD(RD.success(false));
          return;
        } else {
          setFactorsRD(RD.failure(toError(error)));
        }
      }
    },
    [env.VITE_AUTH0_DOMAIN, mfaApi]
  );

  React.useEffect(() => {
    if (
      !hasMfaTokenRD.hasData() ||
      !hasMfaTokenRD.data ||
      !organization.hasData()
    ) {
      return;
    }

    fetchFactors();
  }, [fetchFactors, hasMfaTokenRD, organization]);

  if (isAvelaAdmin) {
    return <NotFound />;
  }

  return (
    <>
      <RemoteDataView
        remoteData={RD.toTuple3(
          hasMfaTokenRD,
          mfaApi.isMfaEnabledRemoteData,
          orgConfigRD
        )}
        error={(err) => <GenericError error={err} />}
        loading={<Loading />}
        reloading={null}
      >
        {([hasMfaToken, isMfaEnabled, mfaConfig]) => {
          if (!isMfaEnabled) {
            return <NotFound />;
          }

          return (
            <AdminPage
              breadcrumb={
                <Breadcrumb
                  items={BC.userProfile.getBreadcrumbsForSecurity(organization)}
                />
              }
              heading={<AdminHeading title="Security" />}
            >
              {hasMfaToken ? (
                <RemoteDataView
                  remoteData={factorsRD}
                  error={(error) => <GenericError error={error} />}
                  loading={<Loading />}
                  reloading={null}
                >
                  {(factors) => {
                    return (
                      <Flex direction="column" gap="6">
                        <Heading as="h2" variant="admin">
                          Multiple factor authentication
                        </Heading>

                        {mfaConfig.enabledFactors.includes("otp") ||
                        isAvelaAdmin ? (
                          <OtpFactorCard
                            factor={factors.find(
                              (f) => f.authenticator_type === "otp" && f.active
                            )}
                            refreshFactors={fetchFactors}
                          />
                        ) : null}

                        {mfaConfig.enabledFactors.includes("phone") &&
                        !isAvelaAdmin ? (
                          <PhoneFactorCard
                            factors={factors.filter(
                              (f) =>
                                f.authenticator_type === "oob" &&
                                (f.oob_channel === "sms" ||
                                  f.oob_channel === "voice") &&
                                f.active
                            )}
                            refreshFactors={fetchFactors}
                          />
                        ) : null}
                      </Flex>
                    );
                  }}
                </RemoteDataView>
              ) : (
                <EmptyState
                  flexGrow="1"
                  alignItems="center"
                  alignSelf="center"
                  justifyContent="center"
                  size="sm"
                  graphic={<Icon boxSize="10" as={RiShieldLine} />}
                  heading="Verify your identity"
                  description={
                    <Flex direction="column" gap="4" color="gray.600">
                      Use one of your multi-factor authentication methods to
                      verify your identity before viewing or changing your
                      security settings.
                    </Flex>
                  }
                >
                  <Flex direction="column" gap="2">
                    <Button
                      variant="outline"
                      colorScheme="gray"
                      isLoading={isReauthenticating}
                      onClick={reauthenticate}
                      loadingText="Redirecting..."
                    >
                      Continue to verification
                    </Button>
                  </Flex>
                </EmptyState>
              )}
            </AdminPage>
          );
        }}
      </RemoteDataView>
    </>
  );
};

const Loading = () => {
  return (
    <Flex direction="column" gap="6">
      <Skeleton maxWidth="20rem" height="2rem" />
      <Skeleton width="100%" height="5rem" />
      <Skeleton width="100%" height="5rem" />
      <Skeleton width="100%" height="5rem" />
    </Flex>
  );
};

function toError(error: unknown): Error {
  return error instanceof Error
    ? error
    : new Error("Unexpected MFA error", { cause: error });
}
